import { useRef, useCallback } from 'react';

interface SwipeGestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  threshold?: number; // Distância mínima em pixels para considerar swipe
  velocity?: number; // Velocidade mínima para considerar swipe rápido
  enabled?: boolean;
}

interface SwipeState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  startTime: number;
  isSwiping: boolean;
}

export const useSwipeGesture = ({
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  threshold = 50,
  velocity = 0.3,
  enabled = true
}: SwipeGestureOptions) => {
  const swipeStateRef = useRef<SwipeState>({
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    startTime: 0,
    isSwiping: false
  });

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled) return;
    
    const touch = e.touches[0];
    swipeStateRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: touch.clientX,
      currentY: touch.clientY,
      startTime: Date.now(),
      isSwiping: false
    };
  }, [enabled]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!enabled || !swipeStateRef.current.startX) return;
    
    const touch = e.touches[0];
    swipeStateRef.current.currentX = touch.clientX;
    swipeStateRef.current.currentY = touch.clientY;
    swipeStateRef.current.isSwiping = true;
  }, [enabled]);

  const handleTouchEnd = useCallback(() => {
    if (!enabled || !swipeStateRef.current.isSwiping) return;

    const { startX, startY, currentX, currentY, startTime } = swipeStateRef.current;
    const deltaX = currentX - startX;
    const deltaY = currentY - startY;
    const deltaTime = Date.now() - startTime;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const speed = distance / deltaTime;

    // Reset state
    swipeStateRef.current = {
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      startTime: 0,
      isSwiping: false
    };

    // Verificar se é um swipe válido
    if (distance < threshold || speed < velocity) {
      return;
    }

    // Determinar direção do swipe
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // Swipe horizontal tem prioridade sobre vertical
    if (absX > absY) {
      if (deltaX > 0 && onSwipeRight) {
        onSwipeRight();
      } else if (deltaX < 0 && onSwipeLeft) {
        onSwipeLeft();
      }
    } else {
      if (deltaY > 0 && onSwipeDown) {
        onSwipeDown();
      } else if (deltaY < 0 && onSwipeUp) {
        onSwipeUp();
      }
    }
  }, [enabled, threshold, velocity, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown]);

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    isSwiping: swipeStateRef.current.isSwiping
  };
};
